import React, { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { UserPlus } from "lucide-react";

interface TeamMemberOption {
  id: string;
  name: string;
  email: string;
}

interface AddTeamMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (data: { userId: string; teamRole: 'MANAGER' | 'TEAM_LEAD' | 'MEMBER' }) => Promise<void>;
  teamName: string;
  members: TeamMemberOption[];
}

const TEAM_ROLES = [
  { value: 'MEMBER', label: 'Member', description: 'Standard member access' },
  { value: 'TEAM_LEAD', label: 'Team Lead', description: 'Coordinates day-to-day work' },
  { value: 'MANAGER', label: 'Manager', description: 'Full team management access' },
] as const;

type TeamRoleValue = typeof TEAM_ROLES[number]['value'];

export function AddTeamMemberModal({
  isOpen,
  onClose,
  onAdd,
  teamName,
  members,
}: AddTeamMemberModalProps) {
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [teamRole, setTeamRole] = useState<TeamRoleValue>('MEMBER');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) => a.name.localeCompare(b.name));
  }, [members]);

  const selectedMember = sortedMembers.find((member) => member.id === selectedMemberId);
  const noMembersAvailable = sortedMembers.length === 0;

  const handleClose = () => {
    if (isSubmitting) return;
    setSelectedMemberId('');
    setTeamRole('MEMBER');
    onClose();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedMemberId) return;

    setIsSubmitting(true);
    try {
      await onAdd({ userId: selectedMemberId, teamRole });
      handleClose();
    } catch (error) {
      console.error('Failed to add team member:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Add Team Member
          </DialogTitle>
          <DialogDescription>
            Add an existing organization member to <strong>{teamName}</strong>.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-6">
            <div className="p-4 bg-muted rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Adding to Team</p>
                  <p className="text-sm text-muted-foreground">{teamName}</p>
                </div>
                <Badge variant="secondary">
                  {TEAM_ROLES.find((role) => role.value === teamRole)?.label}
                </Badge>
              </div>
              {selectedMember && (
                <p className="text-sm text-muted-foreground">
                  Selected member: <strong>{selectedMember.name}</strong>
                  {selectedMember.email ? ` · ${selectedMember.email}` : ''}
                </p>
              )}
            </div>

            <div className="space-y-3">
              <Label>Select Member</Label>
              <Select
                value={selectedMemberId}
                onValueChange={setSelectedMemberId}
                disabled={noMembersAvailable}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder={noMembersAvailable ? 'No available members' : 'Choose a member'} />
                </SelectTrigger>
                <SelectContent>
                  {sortedMembers.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      <div className="flex flex-col">
                        <span className="font-medium">{member.name}</span>
                        <span className="text-xs text-muted-foreground">{member.email}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <Label>Team Role</Label>
              <Select value={teamRole} onValueChange={(value) => setTeamRole(value as TeamRoleValue)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {TEAM_ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      <div className="flex flex-col">
                        <span className="font-medium">{role.label}</span>
                        <span className="text-xs text-muted-foreground">{role.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !selectedMemberId}>
              {isSubmitting ? 'Adding…' : 'Add Member'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
